import Footer, {
  MenuItem as FooterMenuItem
} from "@bmi-digital/components/footer";
import HidePrint from "@bmi-digital/components/hide-print";
import { microCopy } from "@bmi/microcopies";
import { isDefined } from "@bmi/utils";
import { graphql, Link } from "gatsby";
import React from "react";
import { getPathWithCountryCode } from "../utils/path";
import Icon from "./Icon";
import { useSiteContext } from "./Site";
import { toAnchorLinkActionProps } from "./link/utils";
import { ExternalLinkBlock, InternalLinkBlock } from "./link/types";
import type { MainNavigationItem } from "@bmi-digital/components/footer";
import type { PayloadMainFooterNavigation } from "../types/payload";

const parseNavigation = (
  navigationItems: (InternalLinkBlock | ExternalLinkBlock)[],
  countryCode: string
): [FooterMenuItem, ...FooterMenuItem[]] => {
  return navigationItems.map<FooterMenuItem>((navigationItem) => {
    const { label, icon, isLabelHidden } = navigationItem;

    const { download, ...actionProps } = toAnchorLinkActionProps(
      navigationItem,
      countryCode
    );
    const iconProps = icon
      ? { icon: <Icon name={icon} />, isLabelHidden }
      : { icon: undefined, isLabelHidden: false };
    return {
      label,
      ...iconProps,
      ...actionProps,
      gtm: {
        id: "nav-footer1",
        action: actionProps.href || `${actionProps.to}`,
        label
      }
    };
  }) as [FooterMenuItem, ...FooterMenuItem[]];
};

export type FooterMainNavigation = Pick<
  PayloadMainFooterNavigation,
  "label"
> & {
  links: (InternalLinkBlock | ExternalLinkBlock)[];
};

type Props = {
  mainNavigation: FooterMainNavigation[] | null;
  secondaryNavigation: (InternalLinkBlock | ExternalLinkBlock)[] | null;
};

const BmiFooter = ({ mainNavigation, secondaryNavigation }: Props) => {
  const { countryCode, getMicroCopy } = useSiteContext();
  const main: MainNavigationItem[] | undefined = (mainNavigation || [])
    .map<MainNavigationItem | undefined>(
      (navigation: FooterMainNavigation): MainNavigationItem | undefined => {
        return {
          subHeading: navigation.label,
          menuItems: parseNavigation(navigation.links, countryCode)
        };
      }
    )
    .filter(isDefined);

  const secondary = secondaryNavigation
    ? parseNavigation(secondaryNavigation, countryCode)
    : [];

  const sitemapPath = getPathWithCountryCode(countryCode, "sitemap");
  const sitemapLabel = getMicroCopy(microCopy.GLOBAL_SITEMAP);

  secondary.push({
    label: sitemapLabel,
    to: sitemapPath,
    component: Link,
    gtm: {
      id: "nav-footer1",
      action: sitemapPath,
      label: sitemapLabel
    }
  });

  return (
    <HidePrint>
      <Footer
        mainNavigation={main}
        secondaryNavigation={secondary as [FooterMenuItem, ...FooterMenuItem[]]}
      />
    </HidePrint>
  );
};

export default BmiFooter;

export const query = graphql`
  fragment FooterMainNavigationFragment on PayloadMainFooterNavigation {
    label
    links {
      ... on PayloadInternalLinkBlock {
        ...InternalLinkBlockFragment
      }
      ... on PayloadExternalLinkBlock {
        ...ExternalLinkBlockFragment
      }
    }
  }
`;
